import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PERMISSIONS } from '@lms/shared-types';
import { Badge, ConfirmModal, Loader, useToast } from '@lms/ui';
import { SearchEmptyState } from '@/components/SearchEmptyState';
import { SearchInput } from '@/components/SearchInput';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { usePermissions } from '@/hooks/usePermissions';
import { api, listAllResource } from '@/lib/api';
import { matchesSearch } from '@/lib/search-normalize';
import { contractGeneratedDeleteSuccessToast, roleActionErrorToast } from '@/lib/user-feedback';

interface ContractRow {
  id: string;
  code: string;
  status: string;
  generatedAt: string;
  acceptedAt?: string | null;
  studentName: string;
  studentEmail: string;
  courseName: string;
  templateTitle: string;
}

const STATUS_LABELS: Record<string, string> = {
  pending_acceptance: 'Pendiente',
  accepted: 'Aceptado',
  generated: 'Generado',
};

const PER_PAGE = 10;

function statusVariant(status: string): 'success' | 'warning' | 'default' {
  if (status === 'accepted') return 'success';
  if (status === 'pending_acceptance') return 'warning';
  return 'default';
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return (
    parts
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join('') || '?'
  );
}

export function ContratosPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(PERMISSIONS.CONTRATOS_ADMINISTRAR);
  const canManageTemplates = hasPermission(PERMISSIONS.CONTRATOS_PLANTILLAS);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [deleteTarget, setDeleteTarget] = useState<ContractRow | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['contracts'],
    queryFn: () => listAllResource<ContractRow>('/contracts'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api(`/contracts/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      setDeleteTarget(null);
      toast(contractGeneratedDeleteSuccessToast());
      void queryClient.invalidateQueries({ queryKey: ['contracts'] });
    },
    onError: (error) => {
      toast(roleActionErrorToast(error, 'No se pudo eliminar el contrato'));
    },
  });

  const filteredRows = useMemo(() => {
    const all = data?.data ?? [];
    return all.filter((row) =>
      matchesSearch([row.studentName, row.studentEmail, row.courseName, row.code].join(' '), search),
    );
  }, [data?.data, search]);

  const total = filteredRows.length;
  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  const firstItem = total === 0 ? 0 : (currentPage - 1) * PER_PAGE + 1;
  const lastItem = Math.min(currentPage * PER_PAGE, total);

  const rows = useMemo(() => {
    const start = (currentPage - 1) * PER_PAGE;
    return filteredRows.slice(start, start + PER_PAGE);
  }, [filteredRows, currentPage]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  return (
    <div className="lms-contracts-generated lms-site-admin-hub-page font-sans">
      <div className="mb-6 flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
        <div className="flex items-start gap-4">
          <HubBackLink to="/administracion?tab=plugins" label="Volver a Administración" />
          <div className="min-w-0">
            <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">Contratos generados</h1>
            <p className="mt-1 max-w-2xl text-dash-on-surface-variant">
              Contratos de servicios creados automáticamente al matricular alumnos.
            </p>
          </div>
        </div>
        {canManageTemplates ? (
          <Link
            to="/contratos/plantillas"
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-dash-primary px-6 py-3 text-sm font-medium text-dash-primary transition-colors hover:bg-brand-50"
          >
            <span className="material-symbols-outlined text-[1.25rem]" aria-hidden="true">
              description
            </span>
            Plantillas
          </Link>
        ) : null}
      </div>

      <div className="lms-credentials-glass-panel mb-8 overflow-hidden rounded-xl shadow-sm">
        <form
          className="flex flex-col justify-between gap-4 border-b border-dash-outline-variant p-6 lg:flex-row lg:items-center"
          onSubmit={(event) => event.preventDefault()}
        >
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Buscar por alumno, curso o código…"
            aria-label="Buscar contratos"
            className="min-w-[17.5rem] flex-1"
          />
        </form>
      </div>

      {error ? <p className="text-sm text-red-600">No se pudieron cargar los contratos.</p> : null}

      {!error ? (
        <div className="lms-gradebook lms-gradebook--forum">
          <div className="lms-course-forum-card">
            {isLoading ? (
              <div className="flex justify-center py-12">
                <Loader label="Cargando contratos…" />
              </div>
            ) : total === 0 ? (
              <div className="lms-gradebook__table-wrap">
                <div className="lms-gradebook__empty">
                  <div className="lms-gradebook__empty-icon" aria-hidden="true">
                    <span className="material-symbols-outlined" style={{ fontVariationSettings: "'FILL' 1" }}>
                      description
                    </span>
                  </div>
                  {search.trim() ? (
                    <SearchEmptyState
                      term={search.trim()}
                      onClear={() => {
                        setSearch('');
                        setPage(1);
                      }}
                    />
                  ) : (
                    <>
                      <h3>Sin contratos</h3>
                      <p>No hay contratos generados todavía.</p>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <>
                <div className="lms-gradebook__table-wrap">
                  <table className="lms-gradebook__table">
                    <thead>
                      <tr>
                        <th className="lms-gradebook__th" scope="col">
                          Alumno
                        </th>
                        <th className="lms-gradebook__th" scope="col">
                          Código
                        </th>
                        <th className="lms-gradebook__th" scope="col">
                          Curso
                        </th>
                        <th className="lms-gradebook__th" scope="col">
                          Plantilla
                        </th>
                        <th className="lms-gradebook__th" scope="col">
                          Estado
                        </th>
                        <th className="lms-gradebook__th" scope="col">
                          Generado
                        </th>
                        <th className="lms-gradebook__th lms-gradebook__th--actions" scope="col">
                          Acciones
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row) => (
                        <tr key={row.id}>
                          <th className="lms-gradebook__td" scope="row">
                            <div className="lms-gradebook__student">
                              <div className="lms-gradebook__avatar" aria-hidden="true">
                                {initials(row.studentName)}
                              </div>
                              <div className="min-w-0">
                                <Link to={`/contratos/${row.id}`} className="lms-gradebook__name">
                                  {row.studentName}
                                </Link>
                                <div className="lms-gradebook__muted">{row.studentEmail}</div>
                              </div>
                            </div>
                          </th>
                          <td className="lms-gradebook__td lms-gradebook__td--nowrap">{row.code}</td>
                          <td className="lms-gradebook__td">{row.courseName}</td>
                          <td className="lms-gradebook__td">{row.templateTitle}</td>
                          <td className="lms-gradebook__td">
                            <Badge variant={statusVariant(row.status)}>
                              {STATUS_LABELS[row.status] ?? row.status}
                            </Badge>
                          </td>
                          <td className="lms-gradebook__td lms-gradebook__td--nowrap">
                            {new Date(row.generatedAt).toLocaleDateString('es-ES')}
                          </td>
                          <td className="lms-gradebook__td lms-gradebook__td--actions">
                            <div className="flex items-center justify-end gap-1">
                              <Link
                                to={`/contratos/${row.id}`}
                                className="lms-gradebook__action"
                                title="Ver contrato"
                                aria-label={`Ver contrato ${row.code}`}
                              >
                                <span className="material-symbols-outlined" aria-hidden="true">
                                  visibility
                                </span>
                              </Link>
                              {canManage ? (
                                <button
                                  type="button"
                                  className="lms-gradebook__action"
                                  title="Eliminar"
                                  aria-label={`Eliminar contrato ${row.code}`}
                                  onClick={() => setDeleteTarget(row)}
                                >
                                  <span className="material-symbols-outlined" aria-hidden="true">
                                    delete
                                  </span>
                                </button>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="lms-course-forum-card__footer">
                  <span className="lms-course-forum-card__meta">
                    Mostrando <strong>{firstItem}</strong> a <strong>{lastItem}</strong> de <strong>{total}</strong>{' '}
                    resultados
                  </span>
                  <div className="lms-course-forum-card__pager">
                    <button
                      type="button"
                      className="lms-course-forum-card__pager-btn"
                      aria-label="Página anterior"
                      disabled={currentPage <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      <span className="material-symbols-outlined" aria-hidden="true">
                        chevron_left
                      </span>
                    </button>
                    <span className="lms-course-forum-card__pager-current">{currentPage}</span>
                    <button
                      type="button"
                      className="lms-course-forum-card__pager-btn"
                      aria-label="Página siguiente"
                      disabled={currentPage >= totalPages}
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    >
                      <span className="material-symbols-outlined" aria-hidden="true">
                        chevron_right
                      </span>
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      ) : null}

      <ConfirmModal
        open={Boolean(deleteTarget)}
        title="Eliminar contrato"
        description={`¿Eliminar el contrato «${deleteTarget?.code}» de ${deleteTarget?.studentName}? Esta acción no se puede deshacer.`}
        confirmLabel="Eliminar"
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        onClose={() => {
          if (!deleteMutation.isPending) setDeleteTarget(null);
        }}
        loading={deleteMutation.isPending}
      />
    </div>
  );
}
